import clsx from 'clsx'
import {FC, useCallback, useState} from 'react'
import {Dropdown} from 'react-bootstrap'
import {useQuery, useQueryClient} from 'react-query'
import {useNavigate} from 'react-router-dom'
import {toPersianDigits} from '../../apps/accounting-sync/core/format'
import {useAuth} from '../../auth'
import {isOnPremisesInstallation} from '../../auth/core/onPremisesAccess'
import {getNotificationSummary, markNotificationsRead} from '../core/api'
import {NotificationItem} from '../core/models'
import {NOTIFICATION_SETTINGS_PATH, NOTIFICATIONS_PATH} from '../core/paths'
import {NotificationItemView} from './NotificationItemView'
import '../Notifications.scss'

export const NOTIFICATION_SUMMARY_KEY = 'notifications-summary'
const REFRESH_INTERVAL_MS = 60000

/** The header bell: unread badge, the latest notifications and a way to the inbox. */
export const NotificationBell: FC<{buttonClassName?: string}> = ({buttonClassName}) => {
  const {currentUser} = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  // An on-premises install has no notification centre.
  const enabled = !!currentUser && !currentUser.support && !isOnPremisesInstallation(currentUser)

  const summary = useQuery(NOTIFICATION_SUMMARY_KEY, getNotificationSummary, {
    enabled,
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    staleTime: 15000,
    retry: 1,
  })

  const openItem = useCallback(
    async (item: NotificationItem) => {
      setOpen(false)
      if (!item.isRead) {
        try {
          await markNotificationsRead([item.id])
        } catch {
          // reading is best effort; the inbox shows the true state
        }
        queryClient.invalidateQueries(NOTIFICATION_SUMMARY_KEY)
      }
      navigate(item.linkPath || NOTIFICATIONS_PATH)
    },
    [navigate, queryClient]
  )

  if (!enabled || summary.data?.available === false) return null

  const unread = summary.data?.unreadCount || 0
  const latest = summary.data?.latest || []

  return (
    <Dropdown
      show={open}
      onToggle={(next) => setOpen(next)}
      align='end'
      className='notify-bell'
      data-testid='notification-bell'
    >
      <Dropdown.Toggle
        as='button'
        type='button'
        bsPrefix='notify-bell__toggle'
        className={clsx('btn btn-icon position-relative', buttonClassName)}
        aria-label={unread ? `اعلان‌ها، ${toPersianDigits(unread)} خوانده‌نشده` : 'اعلان‌ها'}
        title='اعلان‌ها'
      >
        <i className='las la-bell fs-1' aria-hidden='true' />
        {unread > 0 && (
          <span className='notify-bell__count' data-testid='notification-unread-count'>
            {toPersianDigits(unread > 99 ? '99+' : unread)}
          </span>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu className='notify-menu' renderOnMount={false}>
        <div className='notify-menu__header'>
          <span>اعلان‌ها</span>
          {unread > 0 && (
            <span className='notify-badge is-bad'>{toPersianDigits(unread)} خوانده‌نشده</span>
          )}
        </div>
        <div className='notify-menu__list'>
          {summary.isLoading ? (
            <div className='notify-state py-8'>
              <span className='spinner-border spinner-border-sm text-primary' aria-hidden='true' />
            </div>
          ) : latest.length === 0 ? (
            <div className='notify-state py-8'>
              <i className='las la-bell-slash' aria-hidden='true' />
              <p>هنوز اعلانی برای شما ثبت نشده است.</p>
            </div>
          ) : (
            <ul className='notify-list'>
              {latest.map((item) => (
                <NotificationItemView key={item.id} item={item} compact onOpen={openItem} />
              ))}
            </ul>
          )}
        </div>
        <div className='notify-menu__footer'>
          <button
            type='button'
            className='btn btn-sm btn-light-primary me-2'
            onClick={() => {
              setOpen(false)
              navigate(NOTIFICATIONS_PATH)
            }}
          >
            مشاهده همهٔ اعلان‌ها
          </button>
          <button
            type='button'
            className='btn btn-sm btn-light'
            onClick={() => {
              setOpen(false)
              navigate(NOTIFICATION_SETTINGS_PATH)
            }}
          >
            تنظیمات
          </button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  )
}
